import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Download, Info } from 'lucide-react';
import { trainingDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox, tonedCell } from './DashboardPrimitives';
import { BlockLoader } from '../loading';
import StatTile from './StatTile';
import { NOT_MARKED_SLUG } from './lists/training';
import { useDashboardList } from './useDashboardList';
import { useTableSort, SortTh } from './useTableSort';

/**
 * Dashboard → Training tab.
 *
 * The analytics counterpart to Grow → Offline Trainings. That page is a per-session
 * worklist; this answers the questions it can't — is the programme reaching people,
 * who keeps missing it, whose team is the gap in, and is the data even being entered.
 *
 * Three things a reader must understand, all surfaced in the UI rather than left to
 * a code comment (see the footnote rendered at the bottom):
 *   1. Present / Absent / Not Marked are THREE states, not two. Attendance % is over
 *      MARKED rows only, so an admin's data-entry backlog can't depress a manager's
 *      number — "Not marked" reports that gap on its own.
 *   2. Attendance covers sessions that have already started. Upcoming sessions show a
 *      dash. Registration is the exception — it covers upcoming sessions too, because
 *      registering happens beforehand and an unregistered session is what blocks End Day.
 *   3. Onboarding-batch sessions are excluded, matching the scorecard metric and the
 *      red-flag engine that read the same rows.
 */

type TrainingType = 'in_person' | 'web_based';
type Attendance = 'present' | 'absent' | 'not_marked';
type Registration = 'registered' | 'not_registered' | 'not_marked';

interface Bucket { invited: number; present: number; absent: number; notMarked: number; attendanceRate: number | null }

interface TrainingRow {
  trainingId: string;
  trainingCode: string | null;
  trainingName: string;
  trainingType: TrainingType;
  typeLabel: string;
  scheduledIst: string;
  held: boolean;
  registrationRequired: boolean;
  employeeCode: string;
  employeeName: string;
  store: string;
  city: string;
  managerCode: string | null;
  managerName: string | null;
  /** Nearest Assistant-Manager-role / Trade-Head-role holder above the employee — the
   *  AM / Trade Head tables' grouping key. Absent from an API older than this build. */
  amCode?: string | null;
  amName?: string | null;
  thCode?: string | null;
  thName?: string | null;
  attendance: Attendance;
  registration: Registration;
  markedAt: string | null;
}

interface SessionRow extends Bucket {
  trainingId: string;
  trainingCode: string | null;
  trainingName: string;
  trainingType: TrainingType;
  typeLabel: string;
  scheduledIst: string;
  held: boolean;
  registrationRequired: boolean;
  registered: number;
  registrationRate: number | null;
}
interface EmployeeRow extends Bucket {
  employeeCode: string; employeeName: string; store: string; city: string;
  managerCode: string | null; managerName: string | null;
}
interface ManagerRow extends Bucket {
  managerCode: string | null; managerName: string; employees: number; absentees: number; markingRate: number | null;
}
interface TypeRow { type: TrainingType; label: string; sessions: number; invited: number; present: number; absent: number; notMarked: number; attendanceRate: number | null }

interface DashData {
  month: { ym: string; label: string; startDate: string; endDate: string };
  /** A CRS lookup failed — names / stores / managers below are incomplete. */
  partial: boolean;
  notice: string | null;
  kpis: {
    sessionsTotal: number; sessionsHeld: number; sessionsUpcoming: number;
    employeesInvited: number; invitations: number; heldInvitations: number;
    present: number; absent: number; notMarked: number;
    attendanceRate: number | null; markingRate: number | null;
    absentees: number; missedSessions: number;
    registrationInvites: number; registered: number; registrationPending: number;
    registrationRate: number | null;
  };
  bySession: SessionRow[];
  byEmployee: EmployeeRow[];
  byManager: ManagerRow[];
  /** By Assistant Manager / by Trade Head — only populated when tiers.available; a
   *  project defining neither role keeps the By manager card. */
  byAm?: ManagerRow[];
  byTh?: ManagerRow[];
  tiers?: { available: boolean };
  byType: TypeRow[];
  rows: TrainingRow[];
}

const pct = (v: number | null): string => (v == null ? '—' : `${v}%`);

const ATTENDANCE_LABEL: Record<Attendance, string> = {
  present: 'Present', absent: 'Absent', not_marked: 'Not marked',
};
const REGISTRATION_LABEL: Record<Registration, string> = {
  registered: 'Registered', not_registered: 'Not registered', not_marked: 'Not marked',
};

function Card({ title, subtitle, action, children }: { title: string; subtitle?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Attendance chip. Only the two exceptions are coloured — Present is the normal
 *  outcome and stays neutral, so a scan down the column picks out what needs work. */
function AttendanceChip({ state }: { state: Attendance }) {
  const cls =
    state === 'absent' ? 'bg-rose-50 text-rose-700'
    : state === 'not_marked' ? 'bg-amber-50 text-amber-700'
    : 'bg-gray-100 text-gray-600';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{ATTENDANCE_LABEL[state]}</span>;
}

const TH = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const THR = 'px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TD = 'px-3 py-2 text-sm text-gray-700';
const TDR = 'px-3 py-2 text-right text-sm tabular-nums text-gray-700';

/**
 * Compact numeric column classes for the two SIDE-BY-SIDE cards only.
 *
 * Those cards get roughly half the viewport each, and with full-width padding plus
 * spelled-out headers their last column — Attendance %, the headline number — fell
 * behind a horizontal scroll on any laptop narrower than ~1650px. Tighter padding and
 * two abbreviated headers recover about 90px per table, which is the difference between
 * "reads at a glance" and "scroll to see the point". The Sessions table above is full
 * width and keeps the roomier TH/THR.
 */
const THRC = 'px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TDRC = 'px-2 py-2 text-right text-sm tabular-nums text-gray-700';

/** Rows shown in the Employees / Managers cards before "View all". Matches the ten the
 *  Mood and Attendance watchlist cards cap at, so the dashboard reads consistently. */
const TOP_N = 10;

/** Download + View all pair for a capped list card. `onViewAll` is omitted when the
 *  list already fits, so the button never appears promising rows that aren't there. */
function ListActions({ onDownload, onViewAll }: { onDownload: () => void; onViewAll?: () => void }) {
  const btn = 'inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50';
  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      <button type="button" onClick={onDownload} className={btn} title="Download the full list as CSV">
        <Download className="h-3.5 w-3.5" /> Download
      </button>
      {onViewAll && (
        <button type="button" onClick={onViewAll} className={btn}>
          View All <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * The Employees and Managers tables are each rendered twice — capped inside their card,
 * and in full inside the View-all modal. Extracted so the two copies cannot drift, and
 * so each instance owns its own sort state (sorting the modal must not reorder the card
 * behind it).
 */
function EmployeeTable({
  rows,
  onRowClick,
  // `compact` = rendered in the half-width card rather than the View-all modal. Only
  // the two free-text columns differ: they are the ones with no natural width, so they
  // are what decides whether Att. % survives on screen. The modal is max-w-6xl and has
  // room to show the full store name.
  compact = false,
}: { rows: EmployeeRow[]; onRowClick: (r: EmployeeRow) => void; compact?: boolean }) {
  const storeW = compact ? 'max-w-[120px]' : 'max-w-[240px]';
  const sort = useTableSort(rows, {
    emp: (r: EmployeeRow) => r.employeeName || r.employeeCode,
    store: (r: EmployeeRow) => r.store,
    manager: (r: EmployeeRow) => r.managerName,
    invited: (r: EmployeeRow) => r.invited,
    present: (r: EmployeeRow) => r.present,
    absent: (r: EmployeeRow) => r.absent,
    notMarked: (r: EmployeeRow) => r.notMarked,
    rate: (r: EmployeeRow) => r.attendanceRate,
  });
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <SortTh label="Employee" sortKey="emp" sort={sort} className={TH} />
            <SortTh label="Store" sortKey="store" sort={sort} className={TH} />
            <SortTh label="Manager" sortKey="manager" sort={sort} className={TH} />
            <SortTh label="Invited" sortKey="invited" sort={sort} align="right" className={THRC} />
            <SortTh label="Present" sortKey="present" sort={sort} align="right" className={THRC} />
            <SortTh label="Absent" sortKey="absent" sort={sort} align="right" className={THRC} />
            <SortTh label="Unmarked" sortKey="notMarked" sort={sort} align="right" className={THRC} title="Invitations with no attendance mark entered yet" />
            <SortTh label="Att. %" sortKey="rate" sort={sort} align="right" className={THRC} title="Attendance — Present ÷ (Present + Absent)" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sort.sorted.map((r) => (
            <tr key={r.employeeCode} className="cursor-pointer hover:bg-gray-50" onClick={() => onRowClick(r)}>
              <td className={TD}>
                <div className="font-medium text-gray-900">{r.employeeName}</div>
                <div className="text-[11px] text-gray-400">{r.employeeCode}</div>
              </td>
              {/* max-w is what makes the `truncate` below actually truncate. Without it a
                  table cell just grows to fit, and one long store name ("MALHOTRA RADIO
                  AND WATCH @ STATION RD") stretched this column to a third of the table,
                  pushing Attendance out of view at every viewport width. */}
              <td className={`${TD} ${storeW}`}>
                <div className="truncate" title={r.store || undefined}>{r.store || '—'}</div>
                {r.city && <div className="truncate text-[11px] text-gray-400">{r.city}</div>}
              </td>
              {/* Capped for the same reason as Store: an unbounded name column is what
                  pushes the numbers off the edge, and manager names run long. */}
              <td className={`${TD} max-w-[120px] truncate`} title={r.managerName || undefined}>{r.managerName || '—'}</td>
              <td className={`${TDRC} font-medium`}>{r.invited}</td>
              <td className={TDRC}>{r.present}</td>
              <td className={tonedCell(TDRC, r.absent > 0 && 'font-medium text-rose-700')}>{r.absent}</td>
              <td className={tonedCell(TDRC, r.notMarked > 0 && 'text-amber-700')}>{r.notMarked}</td>
              <td className={TDRC}>{pct(r.attendanceRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One per-person roll-up — By manager, By Assistant Manager or By Trade Head. The three
 *  share every column; only the grouping and the first header differ. */
function ManagerTable({ rows, onRowClick, nameLabel = 'Manager' }: { rows: ManagerRow[]; onRowClick: (r: ManagerRow) => void; nameLabel?: string }) {
  const sort = useTableSort(rows, {
    manager: (r: ManagerRow) => r.managerName,
    employees: (r: ManagerRow) => r.employees,
    invited: (r: ManagerRow) => r.invited,
    present: (r: ManagerRow) => r.present,
    absent: (r: ManagerRow) => r.absent,
    absentees: (r: ManagerRow) => r.absentees,
    notMarked: (r: ManagerRow) => r.notMarked,
    rate: (r: ManagerRow) => r.attendanceRate,
    marking: (r: ManagerRow) => r.markingRate,
  });
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <SortTh label={nameLabel} sortKey="manager" sort={sort} className={TH} />
            <SortTh label="Team" sortKey="employees" sort={sort} align="right" className={THRC} title="Employees invited to at least one held session" />
            <SortTh label="Invited" sortKey="invited" sort={sort} align="right" className={THRC} />
            <SortTh label="Present" sortKey="present" sort={sort} align="right" className={THRC} />
            <SortTh label="Absent" sortKey="absent" sort={sort} align="right" className={THRC} title="Absence records — one per missed session" />
            <SortTh label="People" sortKey="absentees" sort={sort} align="right" className={THRC} title="Absentees — distinct employees who missed at least one session" />
            <SortTh label="Unmarked" sortKey="notMarked" sort={sort} align="right" className={THRC} title="Invitations with no attendance mark entered yet" />
            <SortTh label="Att. %" sortKey="rate" sort={sort} align="right" className={THRC} title="Attendance — Present ÷ (Present + Absent)" />
            <SortTh label="Marked" sortKey="marking" sort={sort} align="right" className={THRC} title="Share of invitations with attendance actually entered" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sort.sorted.map((r) => (
            <tr key={r.managerCode ?? '__unassigned__'} className="cursor-pointer hover:bg-gray-50" onClick={() => onRowClick(r)}>
              <td className={TD}>
                <div className="font-medium text-gray-900">{r.managerName}</div>
                {r.managerCode && <div className="text-[11px] text-gray-400">{r.managerCode}</div>}
              </td>
              <td className={TDRC}>{r.employees}</td>
              <td className={`${TDRC} font-medium`}>{r.invited}</td>
              <td className={TDRC}>{r.present}</td>
              <td className={tonedCell(TDRC, r.absent > 0 && 'font-medium text-rose-700')}>{r.absent}</td>
              <td className={tonedCell(TDRC, r.absentees > 0 && 'text-rose-700')}>{r.absentees}</td>
              <td className={tonedCell(TDRC, r.notMarked > 0 && 'text-amber-700')}>{r.notMarked}</td>
              <td className={TDRC}>{pct(r.attendanceRate)}</td>
              <td className={tonedCell(TDRC, r.markingRate != null && r.markingRate < 100 && 'text-amber-700')}>{pct(r.markingRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The invitation drill-down's table. Its own component so the sort lives and dies with
 * the modal — every drill opens in the server's order — and so a search that briefly
 * matches nothing doesn't throw the chosen sort away (the empty state renders here too).
 */
function InvitationTable({ rows }: { rows: TrainingRow[] }) {
  const sort = useTableSort(rows, {
    emp: (r: TrainingRow) => r.employeeName || r.employeeCode,
    store: (r: TrainingRow) => r.store,
    manager: (r: TrainingRow) => r.managerName,
    session: (r: TrainingRow) => r.trainingName,
    // 'YYYY-MM-DD HH:mm' IST from the server, so it sorts chronologically as a string.
    when: (r: TrainingRow) => r.scheduledIst,
    // Upcoming sessions and sessions with no registration link show a dash, so they
    // sort as missing rather than by a state the cell doesn't display.
    attendance: (r: TrainingRow) => (r.held ? ATTENDANCE_LABEL[r.attendance] : null),
    registration: (r: TrainingRow) => (r.registrationRequired ? REGISTRATION_LABEL[r.registration] : null),
  });
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">Nothing matches that search.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <SortTh label="Employee" sortKey="emp" sort={sort} className={TH} />
            <SortTh label="Store" sortKey="store" sort={sort} className={TH} />
            <SortTh label="Manager" sortKey="manager" sort={sort} className={TH} />
            <SortTh label="Session" sortKey="session" sort={sort} className={TH} />
            <SortTh label="Scheduled (IST)" sortKey="when" sort={sort} className={TH} />
            <SortTh label="Attendance" sortKey="attendance" sort={sort} className={TH} />
            <SortTh label="Registration" sortKey="registration" sort={sort} className={TH} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sort.sorted.map((r) => (
            <tr key={`${r.trainingId}:${r.employeeCode}`}>
              <td className={TD}>
                <div className="font-medium text-gray-900">{r.employeeName}</div>
                <div className="text-[11px] text-gray-400">{r.employeeCode}</div>
              </td>
              <td className={TD}>
                <div>{r.store || '—'}</div>
                {r.city && <div className="text-[11px] text-gray-400">{r.city}</div>}
              </td>
              <td className={TD}>{r.managerName || '—'}</td>
              <td className={TD}>
                <div>{r.trainingName}</div>
                <div className="text-[11px] text-gray-400">{r.trainingCode ?? ''} · {r.typeLabel}</div>
              </td>
              <td className={`${TD} whitespace-nowrap`}>
                {r.scheduledIst}
                {!r.held && <div className="text-[11px] text-primary-600">Upcoming</div>}
              </td>
              <td className={TD}>{r.held ? <AttendanceChip state={r.attendance} /> : <span className="text-gray-400">—</span>}</td>
              <td className={TD}>
                {r.registrationRequired
                  ? <span className={r.registration === 'registered' ? 'text-gray-600' : 'text-amber-700'}>{REGISTRATION_LABEL[r.registration]}</span>
                  : <span className="text-gray-400">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TrainingTab({
  projectId,
  month,
  roleId,
  filters,
}: {
  projectId: number | null;
  month: string;
  roleId?: string;
  filters?: DashRichFilterParams;
}) {
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  // Drill-down modal — a pre-filtered slice of `rows`, searched client-side. Opened
  // from a table row or a bar it is page memory; the Not marked list is opened by the
  // address instead (see `openDrillView`), so the home screen can land on it.
  const [drill, setDrill] = useState<{ title: string; rows: TrainingRow[] } | null>(null);
  const [drillSearch, setDrillSearch] = useState('');
  const { list: listSlug, openList, closeList } = useDashboardList();

  useEffect(() => {
    if (projectId == null) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    trainingDashboardAPI
      .getDashboard({ month, projectId, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== reqRef.current) return;
        setData((res.data?.data ?? null) as DashData | null);
      })
      .catch((err) => {
        if (reqId !== reqRef.current) return;
        setError(err.response?.data?.message || 'Failed to load training dashboard');
        setData(null);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
  }, [month, projectId, roleId, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionRows = data?.bySession ?? [];
  const employeeRows = data?.byEmployee ?? [];
  const managerRows = data?.byManager ?? [];
  // By Assistant Manager + By Trade Head replace By manager when the project defines
  // those roles (see the controller); otherwise By manager stays, exactly as before.
  const amRows = data?.byAm ?? [];
  const thRows = data?.byTh ?? [];
  const tiersOn = !!data?.tiers?.available;
  const typeRows = data?.byType ?? [];
  // Memoized, not re-filtered per render: every table on this tab holds sort state in
  // this component, so a single column-header click or search keystroke would otherwise
  // walk the whole invitation set again for a value only the click handlers read.
  const heldRows = useMemo(() => (data?.rows ?? []).filter((r) => r.held), [data]);
  // The Not marked list: what the headline number and its band of the coverage bar
  // both open.
  const notMarkedRows = useMemo(() => heldRows.filter((r) => r.attendance === 'not_marked'), [heldRows]);
  // The number said beside that list. The server counts it apart from the rows it
  // sends, so it is said (and the tile opens the list) only while the two agree.
  const notMarked = data && data.kpis.notMarked === notMarkedRows.length ? data.kpis.notMarked : null;
  // The held invitations behind one roll-up row, filtered on the same key — direct
  // manager, AM or Trade Head — the row was aggregated on, so the drill's count matches
  // the clicked row. A null code is the row's "Unassigned" / "No …" bucket.
  const rollupDrillRows = (list: 'managers' | 'am' | 'th', r: ManagerRow): TrainingRow[] =>
    heldRows.filter((x) => ((list === 'am' ? x.amCode : list === 'th' ? x.thCode : x.managerCode) ?? null) === r.managerCode);

  // Sort hooks must be unconditional — declared before any early return.
  const sesSort = useTableSort(sessionRows, {
    code: (r: SessionRow) => r.trainingCode,
    name: (r: SessionRow) => r.trainingName,
    type: (r: SessionRow) => r.typeLabel,
    when: (r: SessionRow) => r.scheduledIst,
    invited: (r: SessionRow) => r.invited,
    // An upcoming session shows a dash in these three cells, so it sorts as missing —
    // its not-yet-marked invitations would otherwise float it to the top of Not marked.
    present: (r: SessionRow) => (r.held ? r.present : null),
    absent: (r: SessionRow) => (r.held ? r.absent : null),
    notMarked: (r: SessionRow) => (r.held ? r.notMarked : null),
    rate: (r: SessionRow) => r.attendanceRate,
    reg: (r: SessionRow) => r.registrationRate,
  });
  const typeSort = useTableSort(typeRows, {
    mode: (r: TypeRow) => r.label,
    sessions: (r: TypeRow) => r.sessions,
    invited: (r: TypeRow) => r.invited,
    present: (r: TypeRow) => r.present,
    absent: (r: TypeRow) => r.absent,
    notMarked: (r: TypeRow) => r.notMarked,
    rate: (r: TypeRow) => r.attendanceRate,
  });
  // The Employees and Managers tables own their sort state inside EmployeeTable /
  // ManagerTable, because each is rendered twice — capped in the card, in full in the
  // View-all modal — and the two copies should sort independently.

  // Which "View all" modal is open. One at a time, so a single search box serves all.
  // 'am' / 'th' are the By Assistant Manager / By Trade Head cards.
  const [fullList, setFullList] = useState<'employees' | 'managers' | 'am' | 'th' | null>(null);
  const [fullSearch, setFullSearch] = useState('');
  const openFullList = (which: 'employees' | 'managers' | 'am' | 'th') => {
    setFullSearch('');
    setFullList(which);
  };
  // The roll-up the open modal lists, when it lists one.
  const fullRollup: 'managers' | 'am' | 'th' = fullList === 'am' || fullList === 'th' ? fullList : 'managers';
  const listRows = fullRollup === 'am' ? amRows : fullRollup === 'th' ? thRows : managerRows;

  const fullEmployees = useMemo(() => {
    const q = fullSearch.trim().toLowerCase();
    if (!q) return employeeRows;
    return employeeRows.filter((r) =>
      [r.employeeName, r.employeeCode, r.store, r.city, r.managerName]
        .some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
  }, [employeeRows, fullSearch]);

  const fullManagers = useMemo(() => {
    const q = fullSearch.trim().toLowerCase();
    if (!q) return listRows;
    return listRows.filter((r) =>
      [r.managerName, r.managerCode].some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
  }, [listRows, fullSearch]);

  // The list in the pop-up. One named in the address is DERIVED from it on every
  // render, never copied into state: the month is asked for again a moment after the
  // dashboard opens (when the default Role filter lands), and a list opened once on
  // arrival would be shut by that. It shows once its rows have arrived.
  const openDrillView = useMemo((): { title: string; rows: TrainingRow[] } | null => {
    if (drill) return drill;
    return listSlug === NOT_MARKED_SLUG && notMarkedRows.length > 0
      ? { title: ATTENDANCE_LABEL.not_marked, rows: notMarkedRows }
      : null;
  }, [drill, listSlug, notMarkedRows]);

  // A Not marked list named in the address that this answer will not open (nothing is
  // unmarked this month, or the month failed to load) comes out of the address once the
  // answer is in. Left there, it would open the pop-up by itself the moment a later Month
  // or Role change brings unmarked rows in, with no click. An answer still on its way
  // decides nothing, and a broader answer with no rows means a narrower one has none either.
  const notMarkedWillNotOpen = listSlug === NOT_MARKED_SLUG && !loading && (!!error || data !== null)
    && notMarkedRows.length === 0;
  useEffect(() => {
    if (notMarkedWillNotOpen) closeList();
  }, [notMarkedWillNotOpen, closeList]);

  const drillFiltered = useMemo(() => {
    const q = drillSearch.trim().toLowerCase();
    const rows = openDrillView?.rows ?? [];
    if (!q) return rows;
    return rows.filter((r) =>
      [r.employeeName, r.employeeCode, r.trainingName, r.trainingCode, r.managerName, r.store, r.city]
        .some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
  }, [openDrillView, drillSearch]);

  const openDrill = (title: string, rows: TrainingRow[]) => {
    setDrillSearch('');
    setDrill({ title, rows });
  };
  const closeDrill = () => {
    setDrill(null);
    // Emptied on the way out as well: a list opened by the address has no opening
    // step of its own, and a search left behind would show fewer rows than its number.
    setDrillSearch('');
    if (listSlug === NOT_MARKED_SLUG) closeList();
  };

  const handleDownload = () => {
    if (!data) return;
    const headers = [
      'Training ID', 'Training Name', 'Type', 'Scheduled (IST)', 'Session Status',
      'Employee Code', 'Employee', 'Store', 'City', 'Manager', 'Attendance', 'Registration', 'Last Marked (IST)',
    ];
    const rows = data.rows.map((r) => [
      r.trainingCode ?? '',
      r.trainingName,
      r.typeLabel,
      r.scheduledIst,
      r.held ? 'Held' : 'Upcoming',
      r.employeeCode,
      r.employeeName,
      r.store,
      r.city,
      r.managerName ?? '',
      ATTENDANCE_LABEL[r.attendance],
      // A session with no registration URL has nothing to register for — an empty
      // cell is honest where "Not marked" would read as a missed action.
      r.registrationRequired ? REGISTRATION_LABEL[r.registration] : '',
      // Already IST from the server — see the marked_ist column in the controller.
      r.markedAt ?? '',
    ]);
    downloadCsv(`Training_${data.month.ym}.csv`, buildCsv(headers, rows));
  };

  // Per-card exports of the AGGREGATE rows, not the invitation-level dump the header
  // Download produces. Both export the full list, never the capped ten on screen —
  // downloading a truncated table would be a quietly wrong answer.
  const handleEmployeeDownload = () => {
    if (!data) return;
    const headers = ['Employee Code', 'Employee', 'Store', 'City', 'Manager', 'Invited', 'Present', 'Absent', 'Not Marked', 'Attendance %'];
    const rows = data.byEmployee.map((r) => [
      r.employeeCode, r.employeeName, r.store, r.city, r.managerName ?? '',
      String(r.invited), String(r.present), String(r.absent), String(r.notMarked),
      r.attendanceRate == null ? '' : String(r.attendanceRate),
    ]);
    downloadCsv(`Training_Employees_${data.month.ym}.csv`, buildCsv(headers, rows));
  };

  // One export for all three roll-ups: the list picks the rows, the name column's
  // header and the file name. By manager's file is unchanged.
  const handleManagerDownload = (list: 'managers' | 'am' | 'th') => {
    if (!data) return;
    const who = list === 'am' ? 'Assistant Manager' : list === 'th' ? 'Trade Head' : 'Manager';
    const source = list === 'am' ? amRows : list === 'th' ? thRows : data.byManager;
    const file = list === 'am' ? 'Assistant_Managers' : list === 'th' ? 'Trade_Heads' : 'Managers';
    const headers = [`${who} Code`, who, 'Team', 'Invited', 'Present', 'Absent', 'Absentees', 'Not Marked', 'Attendance %', 'Marked %'];
    const rows = source.map((r) => [
      r.managerCode ?? '', r.managerName, String(r.employees), String(r.invited),
      String(r.present), String(r.absent), String(r.absentees), String(r.notMarked),
      r.attendanceRate == null ? '' : String(r.attendanceRate),
      r.markingRate == null ? '' : String(r.markingRate),
    ]);
    downloadCsv(`Training_${file}_${data.month.ym}.csv`, buildCsv(headers, rows));
  };

  if (projectId == null) {
    return <div className="flex h-40 items-center justify-center text-sm text-gray-500">Select a project to view training.</div>;
  }
  if (loading) {
    return (
      <BlockLoader className="h-72" />
    );
  }
  if (error) return <div className="flex h-40 items-center justify-center text-sm text-rose-600">{error}</div>;
  if (!data) return <div className="flex h-40 items-center justify-center text-sm text-gray-500">No training data.</div>;

  const k = data.kpis;
  const monthLabel = data.month.label;
  const hasRows = data.rows.length > 0;

  // By Assistant Manager / By Trade Head card — By manager's card, over that tier's rows.
  const rollupCard = (list: 'am' | 'th', rows: ManagerRow[]) => (
    <Card
      title={list === 'am' ? 'By Assistant Manager' : 'By Trade Head'}
      subtitle={`${list === 'am' ? "Each Assistant Manager's team" : "Every Assistant Manager's team under each Trade Head, combined"}${rows.length > TOP_N ? `. Top ${TOP_N} of ${rows.length}` : ''}. Click a row for the invitations.`}
      action={
        rows.length > 0 && (
          <ListActions
            onDownload={() => handleManagerDownload(list)}
            onViewAll={rows.length > TOP_N ? () => openFullList(list) : undefined}
          />
        )
      }
    >
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">No sessions have been held yet in {monthLabel}.</p>
      ) : (
        <ManagerTable
          nameLabel={list === 'am' ? 'Assistant Manager' : 'Trade Head'}
          rows={rows.slice(0, TOP_N)}
          onRowClick={(r) => openDrill(r.managerName, rollupDrillRows(list, r))}
        />
      )}
    </Card>
  );
  // Coverage bar denominator. Guarded so a month with only upcoming sessions renders
  // an empty track rather than dividing by zero.
  const coverageTotal = k.present + k.absent + k.notMarked;
  const share = (n: number) => (coverageTotal > 0 ? (n / coverageTotal) * 100 : 0);

  return (
    <div className="space-y-4">
      {/* ─── Header + download ─── */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Training overview</h2>
          <p className="text-xs text-gray-500">Offline training sessions scheduled in {monthLabel}</p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!hasRows}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>

      {/* ─── Incomplete-data banner ─── */}
      {/* Sits ABOVE the tables: without it, a CRS outage renders every employee as a
          bare code under one "Unassigned" manager, which reads as real data rather
          than a failure. The attendance counts are pure Postgres and stay correct,
          so this degrades the tab instead of blanking it — but says so. */}
      {data.partial && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">
              {data.notice || 'Some employee details could not be loaded, so those columns are incomplete.'}
            </p>
          </div>
        </div>
      )}

      {!hasRows ? (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center text-sm text-gray-500 shadow-sm">
          No training sessions scheduled in {monthLabel} for the selected scope.
        </div>
      ) : (
        <>
          {/* ─── KPI strip ─── */}
          {/* Only "Not marked" opens a list: it is the one number here with a list equal
              to it. Sessions held has none (the Sessions table lists upcoming ones too),
              Absentees counts people where every list counts absences, and the rest are
              ratios. */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <StatTile
              label="Sessions held"
              value={String(k.sessionsHeld)}
              helper={k.sessionsUpcoming > 0 ? `${k.sessionsUpcoming} still upcoming` : monthLabel}
            />
            <StatTile
              label="Employees invited"
              value={String(k.employeesInvited)}
              helper={`${k.invitations} invitation${k.invitations === 1 ? '' : 's'}`}
            />
            <StatTile
              label="Attendance"
              value={pct(k.attendanceRate)}
              valueTone="good"
              helper={`${k.present} present of ${k.present + k.absent} marked`}
            />
            {/* Absentees counts distinct PEOPLE; missedSessions counts absence records,
                which is the larger number as soon as anyone misses more than one
                session. Labelling the helper "sessions missed" would be wrong on
                exactly that case, and doubly so when several people miss the same one. */}
            <StatTile
              label="Absentees"
              value={String(k.absentees)}
              valueTone={k.absentees > 0 ? 'bad' : 'default'}
              helper={`${k.missedSessions} absence${k.missedSessions === 1 ? '' : 's'} recorded`}
            />
            {/* markingRate is marked ÷ INVITATIONS, not ÷ sessions — two sessions fully
                marked and one untouched is not "67% of sessions" to any reader. The label
                is the rate alone; of how many invitations is on hover. */}
            <StatTile
              label="Not marked"
              value={String(k.notMarked)}
              valueTone={k.notMarked > 0 ? 'warn' : 'default'}
              helper={`${pct(k.markingRate)} marked`}
              hint={`${pct(k.markingRate)} of ${k.heldInvitations} invitations marked`}
              onClick={notMarked !== null && notMarked > 0 ? () => openList(NOT_MARKED_SLUG) : undefined}
            />
            <StatTile
              label="Registered"
              value={pct(k.registrationRate)}
              valueTone={k.registrationPending > 0 ? 'warn' : 'default'}
              helper={k.registrationInvites > 0 ? `${k.registrationPending} pending` : 'no link on any session'}
            />
          </div>

          {/* ─── Coverage bar ─── */}
          {/* One glance at the month: how the held invitations split three ways. This
              is the visual statement of the tri-state rule the KPIs above encode. */}
          {coverageTotal > 0 && (
            <Card
              title="Attendance coverage"
              subtitle={`${coverageTotal} invitation${coverageTotal === 1 ? '' : 's'} to sessions that have already started. Click a band for the list.`}
            >
              <div className="flex h-7 w-full overflow-hidden rounded-lg bg-gray-100">
                {([
                  { key: 'present' as Attendance, n: k.present, cls: 'bg-emerald-500' },
                  { key: 'absent' as Attendance, n: k.absent, cls: 'bg-rose-500' },
                  { key: 'not_marked' as Attendance, n: k.notMarked, cls: 'bg-amber-400' },
                ]).filter((s) => s.n > 0).map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`${s.cls} h-full transition hover:opacity-80`}
                    style={{ width: `${share(s.n)}%` }}
                    title={`${ATTENDANCE_LABEL[s.key]}: ${s.n}`}
                    // The amber band is the Not marked list, so it opens the way the
                    // headline number does: by the address.
                    onClick={() => (s.key === 'not_marked'
                      ? openList(NOT_MARKED_SLUG)
                      : openDrill(ATTENDANCE_LABEL[s.key], heldRows.filter((r) => r.attendance === s.key)))}
                    aria-label={`${ATTENDANCE_LABEL[s.key]}: ${s.n}`}
                  />
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-gray-500">
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />Present {k.present}</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-500" />Absent {k.absent}</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" />Not marked {k.notMarked}</span>
              </div>
            </Card>
          )}

          {/* ─── By delivery mode ─── */}
          <Card title="By delivery mode" subtitle="Whether classroom and remote sessions land differently. Click a row for the invitations.">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    {/* Opens in its fixed mode order; a third click on a sorted
                        header restores it. */}
                    <SortTh label="Delivery mode" sortKey="mode" sort={typeSort} className={TH} />
                    <SortTh label="Sessions" sortKey="sessions" sort={typeSort} align="right" className={THR} />
                    <SortTh label="Invited" sortKey="invited" sort={typeSort} align="right" className={THR} />
                    <SortTh label="Present" sortKey="present" sort={typeSort} align="right" className={THR} />
                    <SortTh label="Absent" sortKey="absent" sort={typeSort} align="right" className={THR} />
                    <SortTh label="Not marked" sortKey="notMarked" sort={typeSort} align="right" className={THR} />
                    <SortTh label="Attendance" sortKey="rate" sort={typeSort} align="right" className={THR} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {typeSort.sorted.map((t) => (
                    <tr
                      key={t.type}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => openDrill(t.label, heldRows.filter((r) => r.trainingType === t.type))}
                    >
                      <td className={TD}>{t.label}</td>
                      <td className={`${TDR} font-medium`}>{t.sessions}</td>
                      <td className={TDR}>{t.invited}</td>
                      <td className={TDR}>{t.present}</td>
                      <td className={tonedCell(TDR, t.absent > 0 && 'font-medium text-rose-700')}>{t.absent}</td>
                      <td className={tonedCell(TDR, t.notMarked > 0 && 'text-amber-700')}>{t.notMarked}</td>
                      <td className={TDR}>{pct(t.attendanceRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── Sessions ─── */}
          <Card
            title="Sessions"
            subtitle="Every session scheduled this month, upcoming ones included. Click a row for its attendee list."
          >
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <SortTh label="Training ID" sortKey="code" sort={sesSort} className={TH} />
                    <SortTh label="Session" sortKey="name" sort={sesSort} className={TH} />
                    <SortTh label="Mode" sortKey="type" sort={sesSort} className={TH} />
                    <SortTh label="Scheduled (IST)" sortKey="when" sort={sesSort} className={TH} />
                    <SortTh label="Invited" sortKey="invited" sort={sesSort} align="right" className={THR} />
                    <SortTh label="Present" sortKey="present" sort={sesSort} align="right" className={THR} />
                    <SortTh label="Absent" sortKey="absent" sort={sesSort} align="right" className={THR} />
                    <SortTh label="Not marked" sortKey="notMarked" sort={sesSort} align="right" className={THR} />
                    <SortTh label="Attendance" sortKey="rate" sort={sesSort} align="right" className={THR} />
                    <SortTh label="Registered" sortKey="reg" sort={sesSort} align="right" className={THR} title="Only sessions that carry a registration link" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sesSort.sorted.map((r) => (
                    <tr
                      key={r.trainingId}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => openDrill(r.trainingName, data.rows.filter((x) => x.trainingId === r.trainingId))}
                    >
                      <td className={tonedCell(TD, 'whitespace-nowrap font-medium text-gray-900')}>{r.trainingCode ?? '—'}</td>
                      <td className={TD}>
                        <div className="font-medium text-gray-900">{r.trainingName}</div>
                        {!r.held && <span className="text-[11px] text-primary-600">Upcoming</span>}
                      </td>
                      <td className={TD}>{r.typeLabel}</td>
                      <td className={`${TD} whitespace-nowrap`}>{r.scheduledIst}</td>
                      <td className={`${TDR} font-medium`}>{r.invited}</td>
                      {/* An upcoming session has nothing to report yet — a dash, not a
                          zero, which would read as "nobody turned up". */}
                      <td className={TDR}>{r.held ? r.present : '—'}</td>
                      <td className={tonedCell(TDR, r.held && r.absent > 0 && 'font-medium text-rose-700')}>{r.held ? r.absent : '—'}</td>
                      <td className={tonedCell(TDR, r.held && r.notMarked > 0 && 'text-amber-700')}>{r.held ? r.notMarked : '—'}</td>
                      <td className={TDR}>{r.held ? pct(r.attendanceRate) : '—'}</td>
                      <td className={TDR}>{r.registrationRequired ? pct(r.registrationRate) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── Employees + Managers, side by side ─── */}
          {/* Paired in one row so both are visible without scrolling past one to reach
              the other — they answer the two halves of the same question (who is
              missing training, and whose team the gap sits in).
              Pairs from lg: (1024px), not xl: (1280px). At xl: they stacked on any
              window narrower than 1280 CSS px — a non-maximized window, a 13" laptop,
              or browser zoom above 100% — which is common enough that the intended
              side-by-side reading was frequently not what people actually saw. Each
              card keeps its own overflow-x-auto, so the trade at narrow widths is a
              scroll INSIDE a card rather than losing the pairing altogether.
              With the By Assistant Manager / By Trade Head pair below, By manager is
              dropped and this card takes the full width. */}
          <div className={tiersOn ? '' : 'grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start'}>
            <Card
              title="Employees missing training"
              subtitle={`Worst first${employeeRows.length > TOP_N ? `, top ${TOP_N} of ${employeeRows.length}` : ''}. Click a row for that employee's sessions.`}
              action={
                employeeRows.length > 0 && (
                  <ListActions
                    onDownload={handleEmployeeDownload}
                    onViewAll={employeeRows.length > TOP_N ? () => openFullList('employees') : undefined}
                  />
                )
              }
            >
              {employeeRows.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">No sessions have been held yet in {monthLabel}.</p>
              ) : (
                // Capped so this card and the one beside it stay a readable height —
                // the full list lives behind View all, and Download always takes
                // everything regardless of what is on screen.
                <EmployeeTable
                  compact
                  rows={employeeRows.slice(0, TOP_N)}
                  // heldRows, not data.rows: these counts are held-only, so opening the
                  // drill over every invitation would title the modal with a number that
                  // contradicts the Invited cell just clicked.
                  onRowClick={(r) => openDrill(r.employeeName, heldRows.filter((x) => x.employeeCode === r.employeeCode))}
                />
              )}
            </Card>

            {!tiersOn && (
              <Card
                title="By manager"
                subtitle={`Where the gap is concentrated, and who hasn't entered their marks${managerRows.length > TOP_N ? `. Top ${TOP_N} of ${managerRows.length}` : ''}. Click a row for the invitations.`}
                action={
                  managerRows.length > 0 && (
                    <ListActions
                      onDownload={() => handleManagerDownload('managers')}
                      onViewAll={managerRows.length > TOP_N ? () => openFullList('managers') : undefined}
                    />
                  )
                }
              >
                {managerRows.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">No sessions have been held yet in {monthLabel}.</p>
                ) : (
                  <ManagerTable
                    rows={managerRows.slice(0, TOP_N)}
                    onRowClick={(r) => openDrill(r.managerName, rollupDrillRows('managers', r))}
                  />
                )}
              </Card>
            )}
          </div>

          {/* ─── By Assistant Manager + By Trade Head, side by side ─── */}
          {/* The same held invitations counted at two levels — each AM, and the Trade Head
              above them — so each table reconciles with the KPIs on its own. Only when the
              project defines those roles; otherwise By manager (above) stands in. */}
          {tiersOn && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
              {rollupCard('am', amRows)}
              {rollupCard('th', thRows)}
            </div>
          )}

          {/* ─── Footnote ─── */}
          {/* The definitions a reader needs to trust the numbers. Rendered rather than
              left in a code comment because every one of them is a question this tab
              will otherwise be asked. */}
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <div className="space-y-1 text-[11px] leading-relaxed text-gray-500">
                <p><strong className="text-gray-600">Attendance</strong> is Present ÷ (Present + Absent) — marked rows only, so an un-entered mark never counts as an absence. <strong className="text-gray-600">Not marked</strong> reports those rows on their own.</p>
                <p><strong className="text-gray-600">Attendance figures cover sessions that have already started;</strong> upcoming sessions show a dash. Registration is counted on upcoming sessions too, and only on sessions that carry a registration link.</p>
                <p><strong className="text-gray-600">Manager</strong> is the employee's current reporting manager, resolved live — so a manager change re-attributes their earlier sessions.</p>
                {tiersOn && (
                  <p><strong className="text-gray-600">Assistant Manager</strong> and <strong className="text-gray-600">Trade Head</strong> are the nearest people above the employee who hold those roles; a Trade Head's row combines every AM team under them.</p>
                )}
                <p>Cancelled sessions and onboarding-batch sessions are excluded, matching the scorecard's training metric and the red-flag engine. Sessions with nobody assigned yet do not appear.</p>
                <p><strong className="text-gray-600">This will not tie out exactly to the scorecard or the Red Flag tab.</strong> Training Participation on the scorecard divides by every invitation in the month, including upcoming and un-marked ones; red flags group same-day absences into one. All three are measuring different things on purpose.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── View all (full list) ─── */}
      {/* Renders the SAME table component the card does, over every row rather than the
          capped ten. Clicking a row still opens the invitation drill-down, so the modal
          is a bigger window onto the card, not a dead end. */}
      {fullList && (
        <Modal
          title={
            fullList === 'employees'
              ? `Employees missing training — ${fullEmployees.length} of ${employeeRows.length}`
              : `${fullRollup === 'am' ? 'By Assistant Manager' : fullRollup === 'th' ? 'By Trade Head' : 'By manager'} — ${fullManagers.length} of ${listRows.length}`
          }
          onClose={() => setFullList(null)}
          maxWidth="max-w-6xl"
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="flex-1">
              <SearchBox
                value={fullSearch}
                onChange={setFullSearch}
                placeholder={fullList === 'employees' ? 'Search employee, code, store, city or manager…' : 'Search manager or code…'}
              />
            </div>
            <button
              type="button"
              onClick={() => (fullList === 'employees' ? handleEmployeeDownload() : handleManagerDownload(fullRollup))}
              className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50"
              title="Download the full list as CSV"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          </div>
          {fullList === 'employees' ? (
            fullEmployees.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">Nothing matches that search.</p>
            ) : (
              <EmployeeTable
                rows={fullEmployees}
                onRowClick={(r) => openDrill(r.employeeName, heldRows.filter((x) => x.employeeCode === r.employeeCode))}
              />
            )
          ) : fullManagers.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">Nothing matches that search.</p>
          ) : (
            <ManagerTable
              nameLabel={fullRollup === 'am' ? 'Assistant Manager' : fullRollup === 'th' ? 'Trade Head' : 'Manager'}
              rows={fullManagers}
              onRowClick={(r) => openDrill(r.managerName, rollupDrillRows(fullRollup, r))}
            />
          )}
        </Modal>
      )}

      {/* ─── Drill-down ─── */}
      {openDrillView && (
        <Modal title={`${openDrillView.title} — ${openDrillView.rows.length} invitation${openDrillView.rows.length === 1 ? '' : 's'}`} onClose={closeDrill} maxWidth="max-w-5xl">
          <div className="mb-3">
            <SearchBox value={drillSearch} onChange={setDrillSearch} placeholder="Search employee, session, store or manager…" />
          </div>
          <InvitationTable rows={drillFiltered} />
        </Modal>
      )}
    </div>
  );
}
